/* eslint-disable prefer-promise-reject-errors */
import { setShowClosePositionPopup } from 'actions/tradingView';
import {
  IBrokerConnectionAdapterHost,
  IndividualPosition,
  TradingTerminalFeatureset,
  TradingTerminalWidgetOptions,
  Position,
  Order,
  OrderTemplate,
  Brackets,
} from 'charting_library_trading_platform/charting_library';
import { CHARTS_API } from 'constants/api';
import { isCI, isDevnet, isProd } from 'constants/constants';
import { getGrowthBookFeatureValue } from 'growthbook';
import store from 'store';

import { Broker } from './broker';
import { brokerCallbacks, getChartBroker, setChartBroker } from './GlobalBrokerObject';
import datafeed from './newDatafeed';
import { createSaveLoadAdapter } from './saveLoadAdapter';

const isProdBackup = process.env.REACT_APP_ENV === 'production-backup';

const prependAppToUrl = url => (isProdBackup || isCI ? `/${url}` : `/app/${url}`);

const chartConfig = ({
  symbol,
  interval,
  themeObj,
  userId,
  activeTab,
  activeTheme,
  timeframe,
  contractType,
  isNewTab,
  container,
  containerType = 'default',
}): TradingTerminalWidgetOptions => {
  // eslint-disable-next-line no-console
  // console.log('DEBUG ,chartConfig', symbol, interval, themeObj, userId, activeTab);
  let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone && timezone.toLowerCase() === 'asia/calcutta') {
    timezone = 'Asia/Kolkata';
  }

  const userIdPrefix = isProd
    ? `delta-${activeTab}-${containerType}-200323`
    : `delta-testnet-${activeTab}-${containerType}-200323`;

  const saveLoadAdapter = createSaveLoadAdapter(container);

  const disabledFeatures: TradingTerminalFeatureset[] = [
    'remove_library_container_border',
    'header_undo_redo',
    'header_saveload',
    'border_around_the_chart',
    'items_favoriting',
    'trading_account_manager',
    'right_toolbar',
    'display_market_status',
    'legend_inplace_edit',
    'header_screenshot',
    'symbol_search_hot_key',
    'buy_sell_buttons',
    'trading_notifications',
  ];

  // Only disable symbol search features when NOT in new tab mode
  if (!isNewTab) {
    disabledFeatures.push('header_symbol_search');
  }

  if (contractType === 'tracker') {
    disabledFeatures.push('support_multicharts');
    disabledFeatures.push('header_layouttoggle');
  }

  // if (isMobileDeviceOnly()) {
  //   disabledFeatures.push('header_fullscreen_button');
  // }
  const isChartBrokerEnabledFromGB = getGrowthBookFeatureValue('is_chart_broker_enabled');
  const isBrokerEnabled =
    isChartBrokerEnabledFromGB &&
    (containerType === 'futures' || containerType === 'default');

  const showLegendForOptions =
    contractType === 'call_options' ||
    contractType === 'put_options' ||
    contractType === 'move_options';

  const legendOverrides = showLegendForOptions
    ? {
        'mainSeriesProperties.statusViewStyle.showSymbol': true,
        'paneProperties.legendProperties.showLegend': true,
        'paneProperties.legendProperties.showSeriesTitle': true,
      }
    : {};

  return {
    debug: isDevnet || isCI,
    symbol,
    interval,
    container: container === 'default' ? 'tv_chart_container' : container,
    datafeed: datafeed(contractType, isNewTab),
    broker_factory: isBrokerEnabled
      ? (host: IBrokerConnectionAdapterHost) => {
          const broker = new Broker(host);
          setChartBroker(broker);
          return broker;
        }
      : undefined,
    debug_broker: 'broker-only',
    library_path: prependAppToUrl('charting_library_trading_platform/'),
    custom_css_url: prependAppToUrl(
      `charting_library_trading_platform/customStyles.css?v=${Date.now()}`
    ),
    locale: 'en',
    disabled_features: disabledFeatures,
    enabled_features: [
      'hide_last_na_study_output',
      'dont_show_boolean_study_arguments',
      'side_toolbar_in_fullscreen_mode',
      'save_chart_properties_to_local_storage',
      'volume_force_overlay',
      'chart_template_storage',
      'create_volume_indicator_by_default',
      'use_localstorage_for_settings',
      'header_screenshot',
      'hide_object_tree_and_price_scale_exchange_label',
      'auto_enable_symbol_labels',
      'header_in_fullscreen_mode',
      'adaptive_logo',
      'horz_touch_drag_scroll',
      'iframe_loading_compatibility_mode',
      'show_zoom_and_move_buttons_on_touch',
      'symbol_info',
      'show_object_tree',
      'header_compare',
    ],
    load_last_chart: true,
    save_load_adapter: saveLoadAdapter,
    charts_storage_url: CHARTS_API.STORAGE,
    charts_storage_api_version: '1.1',
    auto_save_delay: 1,
    autosize: true,
    timezone,
    timeframe,
    theme: activeTheme,
    broker_config: {
      configFlags: {
        supportPositionBrackets: true,
        supportReversePosition: false,
        supportClosePosition: true,
      },
      customUI: {
        showOrderDialog: (order: OrderTemplate | Order) => {
          return new Promise((resolve, reject) => {
            getChartBroker()
              ?.modifyOrder?.(order as Order)
              .then(() => {
                resolve(true);
              })
              .catch(() => {
                reject(false);
              });
          });
        },
        showPositionDialog: (
          position: Position | IndividualPosition,
          brackets: Brackets
        ) => {
          if (!brackets.takeProfit && !brackets.stopLoss) {
            return new Promise((resolve, reject) => {
              reject(false);
            });
          }
          return new Promise((resolve, reject) => {
            getChartBroker()
              ?.editPositionBrackets?.(position.id, brackets)
              .then(() => {
                resolve(true);
              })
              .catch(() => {
                reject(false);
              });
          });
        },
        showClosePositionDialog: () => {
          return new Promise((resolve, reject) => {
            store.dispatch(setShowClosePositionPopup(true));
            brokerCallbacks.closePosition.resolve = resolve;
            brokerCallbacks.closePosition.reject = reject;
          });
        },
        showCancelOrderDialog: (order: Order) => {
          return new Promise((resolve, reject) => {
            getChartBroker()
              ?.cancelOrder?.(order.id)
              .then(() => {
                resolve(true);
              })
              .catch(() => {
                reject(false);
              });
          });
        },
      },
    },
    custom_font_family: 'Aileron',
    drawings_access: {
      type: 'black',
      tools: [],
    },
    client_id: 'delta.exchange',
    user_id: `${userIdPrefix}-${userId || 'public'}`,
    loading_screen: {
      backgroundColor: themeObj.primaryBackground,
      foregroundColor: themeObj.primaryTheme,
    },
    time_frames: [],
    favorites: {
      chartTypes: ['Candles'],
    },
    overrides: {
      // 'paneProperties.backgroundType': 'solid',
      'paneProperties.background': themeObj.primaryBackground,
      'paneProperties.vertGridProperties.color': themeObj.separator,
      'paneProperties.horzGridProperties.color': themeObj.separator,
      'paneProperties.vertGridProperties.style': 1,
      'paneProperties.horzGridProperties.style': 1,
      'paneProperties.bottomMargin': 5,

      // Select chart type
      'mainSeriesProperties.style': 1,
      'mainSeriesProperties.showCountdown': true,
      // Bar style
      'mainSeriesProperties.barStyle.upColor': themeObj.positive,
      'mainSeriesProperties.barStyle.downColor': themeObj.negative,
      'mainSeriesProperties.barStyle.barColorsOnPrevClose': false,
      'mainSeriesProperties.barStyle.dontDrawOpen': true,
      // Candle Style
      'mainSeriesProperties.candleStyle.upColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.downColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.drawWick': true,
      'mainSeriesProperties.candleStyle.wickUpColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.wickDownColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.barColorsOnPrevClose': false,
      // Hollow Candle Style
      'mainSeriesProperties.hollowCandleStyle.upColor': themeObj.positive,
      'mainSeriesProperties.hollowCandleStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.hollowCandleStyle.downColor': themeObj.negative,
      'mainSeriesProperties.hollowCandleStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.hollowCandleStyle.drawWick': false,
      // Heikin Ashi styles
      'mainSeriesProperties.haStyle.upColor': themeObj.positive,
      'mainSeriesProperties.haStyle.downColor': themeObj.negative,
      'mainSeriesProperties.haStyle.drawWick': true,
      'mainSeriesProperties.haStyle.drawBorder': true,
      'mainSeriesProperties.haStyle.borderColor': '',
      'mainSeriesProperties.haStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.haStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.haStyle.wickUpColor': themeObj.positive,
      'mainSeriesProperties.haStyle.wickDownColor': themeObj.negative,
      'mainSeriesProperties.haStyle.barColorsOnPrevClose': false,
      // Area Style
      'mainSeriesProperties.areaStyle.color1': themeObj.positive,
      'mainSeriesProperties.areaStyle.color2': themeObj.negative,
      'mainSeriesProperties.areaStyle.linecolor': themeObj.negative,
      // Line styles
      'mainSeriesProperties.lineStyle.color': themeObj.negative,
      'mainSeriesProperties.lineStyle.linestyle': 0,
      'mainSeriesProperties.lineStyle.linewidth': 1,
      // Baseline styles
      'mainSeriesProperties.baselineStyle.baselineColor': themeObj.primaryText,
      'mainSeriesProperties.baselineStyle.topFillColor1': themeObj.positive,
      'mainSeriesProperties.baselineStyle.topFillColor2': 'rgba( 83, 185, 135, 0.1)',
      'mainSeriesProperties.baselineStyle.bottomFillColor1': themeObj.negative,
      'mainSeriesProperties.baselineStyle.bottomFillColor2': 'rgba( 235, 77, 92, 0.1)',
      'mainSeriesProperties.baselineStyle.topLineColor': themeObj.positive,
      'mainSeriesProperties.baselineStyle.bottomLineColor': themeObj.negative,
      // Legend properties
      'mainSeriesProperties.statusViewStyle.symbolTextSource': 'ticker',
      ...legendOverrides,
      'paneProperties.legendProperties.showSeriesOHLC': true,
      'paneProperties.legendProperties.showStudyTitles': true,
      'paneProperties.legendProperties.showStudyValues': true,
      'paneProperties.legendProperties.showBarChange': true,
      'paneProperties.legendProperties.showPercentageChange': true,
      'scalesProperties.fontSize': 12,
      'scalesProperties.showSymbolLabels': false,
      'scalesProperties.showStudyLastValue': true,
      'symbolWatermarkProperties.color': 'rgba(110, 110, 110, 0.1)',
      volumePaneSize: 'small',
      'scalesProperties.lineColor': themeObj.separator,
      'scalesProperties.textColor': themeObj.secondaryText,
      'paneProperties.crossHairProperties.color': themeObj.secondaryText,
      'paneProperties.topMargin': 0,
    },
    studies_overrides: {
      'volume.volume.color.0': themeObj.negative,
      'volume.volume.color.1': themeObj.positive,
      'volume.precision': 2,
    },
    symbol_search_request_delay: 1000,
  };
};

export default chartConfig;
